import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  InputBase,
  IconButton,
  styled,
  Slide,
  AppBar,
  Toolbar,
  Paper,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  useMediaQuery,
  useTheme,
  alpha,
  Tooltip,
} from "@mui/material";
import {
  Search as SearchIcon,
  Close as CloseIcon,
  FilterList as FilterIcon,
  Book as BookIcon,
  Headphones as HeadphonesIcon,
  LocalOffer as PriceIcon,
  ArrowBack as ArrowBackIcon,
} from "@mui/icons-material";
import { useNavigate, useLocation } from "react-router-dom";
import SearchSuggestions from "./SearchSuggestions";
import useSearch from "../../hooks/useSearch";
import useSearchHistory from "../../hooks/useSearchHistory";
import { useTranslation } from "react-i18next";

const SearchContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  backgroundColor: alpha(theme.palette.background.default, 0.9),
  padding: "4px 16px",
  borderRadius: theme.shape.borderRadius * 3,
  width: "40%",
  border: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
  transition: "all 0.3s ease",
  
  "&:hover": {
    backgroundColor: theme.palette.background.default,
    borderColor: theme.palette.text.secondary,
    boxShadow: `0 2px 6px ${alpha(theme.palette.common.black, 0.05)}`,
  },
  
  "&:focus-within": {
    borderColor: theme.palette.primary.main,
    boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.2)}`,
    backgroundColor: theme.palette.background.paper,
  },
  
  [theme.breakpoints.down("md")]: {
    width: "35%",
  },
  
  [theme.breakpoints.down("sm")]: {
    display: "none",
  },
}));

const MobileSearchAppBar = styled(AppBar)(({ theme }) => ({
  position: "fixed",
  top: 0,
  backgroundColor: theme.palette.background.paper,
  boxShadow: "none",
  borderBottom: `1px solid ${theme.palette.divider}`,
  zIndex: theme.zIndex.appBar + 1,
}));

const MobileSearchContainer = styled(Box)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  backgroundColor: alpha(theme.palette.background.default, 0.9),
  padding: "8px 12px",
  borderRadius: theme.shape.borderRadius * 2,
  width: "100%",
  margin: "8px 0",
  border: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
  transition: "all 0.3s ease",
  
  "&:focus-within": {
    borderColor: theme.palette.primary.main,
    boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.2)}`,
    backgroundColor: theme.palette.background.paper,
  },
  
  [theme.breakpoints.down(400)]: {
    padding: "6px 10px",
    margin: "6px 0",
  },
}));

const SearchInput = styled(InputBase)(({ theme }) => ({
  flex: 1,
  marginLeft: theme.spacing(1),
  "& input": {
    padding: theme.spacing(1, 0),
    fontSize: "0.95rem",
    transition: "font-size 0.2s ease",
    
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(0.75, 0),
      fontSize: "0.875rem",
    },
    
    [theme.breakpoints.down(400)]: {
      padding: theme.spacing(0.5, 0),
      fontSize: "0.8125rem",
    },
    
    "&::placeholder": {
      opacity: 0.7,
      fontStyle: "italic",
      transition: "opacity 0.2s ease",
    },
    
    "&:focus::placeholder": {
      opacity: 0.5,
    },
  },
}));

const SearchWrapper = styled(Paper)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  height: 40,
  padding: theme.spacing(0, 1),
  borderRadius: theme.shape.borderRadius * 3,
  backgroundColor: alpha(theme.palette.background.default, 0.9),
  border: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
  transition: "all 0.3s ease",
  
  "&:hover": {
    borderColor: theme.palette.text.secondary,
    boxShadow: `0 2px 6px ${alpha(theme.palette.common.black, 0.05)}`,
  },
  
  "&:focus-within": {
    borderColor: theme.palette.primary.main,
    boxShadow: `0 0 0 2px ${alpha(theme.palette.primary.main, 0.2)}`,
    backgroundColor: theme.palette.background.paper,
  },
  
  [theme.breakpoints.down("sm")]: {
    height: 36,
  },
  
  [theme.breakpoints.down(400)]: {
    height: 32,
    padding: theme.spacing(0, 0.5),
  },
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
  flex: 1,
  marginLeft: theme.spacing(1),
  "& input": {
    fontSize: "0.95rem",
    transition: "font-size 0.2s ease",
    
    [theme.breakpoints.down("sm")]: {
      fontSize: "0.875rem",
    },
    
    [theme.breakpoints.down(400)]: {
      fontSize: "0.8125rem",
    },
  },
}));

const FilterButton = styled(IconButton)(({ theme }) => ({
  marginRight: theme.spacing(0.5),
  padding: theme.spacing(0.5),
  transition: "all 0.2s ease",
  
  "&:hover": {
    backgroundColor: alpha(theme.palette.primary.main, 0.1),
    transform: "rotate(10deg) scale(1.05)",
  },
  
  [theme.breakpoints.down(400)]: {
    padding: theme.spacing(0.25),
  },
}));

const StyledMenu = styled(Menu)(({ theme }) => ({
  "& .MuiPaper-root": {
    borderRadius: theme.shape.borderRadius * 2,
    marginTop: theme.spacing(1),
    minWidth: 180,
    boxShadow: theme.shadows[4],
    padding: theme.spacing(1, 0),
    
    [theme.breakpoints.down("sm")]: {
      minWidth: 160,
    },
  },
  
  "& .MuiMenuItem-root": {
    padding: theme.spacing(1, 2),
    transition: "background-color 0.2s ease",
    
    "&:hover": {
      backgroundColor: alpha(theme.palette.primary.main, 0.08),
    },
    
    "&.Mui-selected": {
      backgroundColor: alpha(theme.palette.primary.main, 0.12),
      "&:hover": {
        backgroundColor: alpha(theme.palette.primary.main, 0.16),
      },
    },
    
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(0.75, 1.5),
      minHeight: 40,
    },
    
    [theme.breakpoints.down(400)]: {
      padding: theme.spacing(0.5, 1),
      minHeight: 36,
    },
  },
  
  "& .MuiListItemIcon-root": {
    minWidth: 36,
    
    [theme.breakpoints.down("sm")]: {
      minWidth: 32,
    },
  },
}));

const ActionButton = styled(IconButton)(({ theme }) => ({
  padding: theme.spacing(0.5),
  color: theme.palette.text.secondary,
  transition: 'all 0.2s ease',
  
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.1),
    color: theme.palette.primary.main,
  },
  
  [theme.breakpoints.down(400)]: {
    padding: theme.spacing(0.25),
  },
}));

const MobileToolbar = styled(Toolbar)(({ theme }) => ({
  minHeight: 56,
  padding: theme.spacing(0, 2),
  
  [theme.breakpoints.down("sm")]: {
    minHeight: 52,
    padding: theme.spacing(0, 1),
  },
  
  [theme.breakpoints.down(400)]: {
    minHeight: 48,
    padding: theme.spacing(0, 0.5),
  },
}));

const SearchBar = ({
  autoFocus,
  onClose,
  fullWidth,
  immediateSearch = false,
  onSearch,
  onFormatChange,
  initialFormat = "PDF",
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isExtraSmall = useMediaQuery(theme.breakpoints.down(400));
  const [mobileSearchOpen, setMobileSearchOpen] = useState(immediateSearch);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const searchContainerRef = useRef(null);
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const location = useLocation();
  const [searchTerm, setSearchTerm] = useState("");
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedFormat, setSelectedFormat] = useState(initialFormat);
  const { t } = useTranslation();

  const {
    searchQuery,
    setSearchQuery,
    format,
    setFormat,
    results,
    loading,
    error,
  } = useSearch(initialFormat);

  const { searchHistory, addToHistory, removeFromHistory, clearHistory } =
    useSearchHistory();

  // Extract search term from URL on mount
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const searchParam = params.get("search");
    if (searchParam) {
      setSearchTerm(searchParam);
    }
  }, [location.search]);

  const handleSearchClose = () => {
    setMobileSearchOpen(false);
    setShowSuggestions(false);
    if (onClose) onClose();
  };

  const handleSearchFocus = () => {
    setShowSuggestions(true);
  };

  const handleSearchChange = (event) => {
    const newValue = event.target.value;
    setSearchTerm(newValue);
    setSearchQuery(newValue);
    setShowSuggestions(true);

    // Remove immediate search for mobile view
    if (immediateSearch && !isMobile) {
      // Now isMobile is defined
      clearTimeout(window.searchTimeout);
      window.searchTimeout = setTimeout(() => {
        if (onSearch) {
          onSearch(newValue, format);
        }
      }, 500);
    }
  };

  const handleViewAll = () => {
    addToHistory(searchQuery);
    // Only navigate to specific page when "View all" is clicked
    const targetPath =
      format === "PDF"
        ? "/books"
        : format === "Audiobook"
          ? "/audio-books"
          : "/free-books";
    navigate(`${targetPath}?search=${encodeURIComponent(searchQuery)}`);
    setShowSuggestions(false);
    setMobileSearchOpen(false);
  };

  const handleBookSelect = (book) => {
    addToHistory(searchQuery);
    // Navigate to book details
    navigate(`books/${book.id}`);
    setShowSuggestions(false);
    setMobileSearchOpen(false);
  };

  const handleHistoryItemClick = (query) => {
    setSearchQuery(query);
    setShowSuggestions(true);
  };

  const handleSearch = () => {
    if (onSearch) {
      onSearch(searchTerm, selectedFormat);
    }
  };

  const handleClear = () => {
    setSearchTerm("");
    if (onSearch) {
      onSearch("", selectedFormat);
    }
  };

  const handleKeyPress = (event) => {
    if (event.key === "Enter") {
      handleSearch();
    }
  };

  // Handle filter menu
  const handleFilterClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleFilterClose = () => {
    setAnchorEl(null);
  };

  const handleFormatSelect = (newFormat) => {
    setFormat(newFormat);
    setSelectedFormat(newFormat);
    if (onFormatChange) {
      onFormatChange(newFormat);
    }
    handleFilterClose();

    // Only update the search results, don't navigate
    if (searchQuery.trim()) {
      setSearchQuery(searchQuery); // This will trigger a new search with the new format
    }
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        searchContainerRef.current &&
        !searchContainerRef.current.contains(event.target)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (immediateSearch) {
      setMobileSearchOpen(true);
    }
  }, [immediateSearch]);

  // Add a handler for mobile search submission
  const handleMobileSearch = () => {
    if (searchTerm.trim()) {
      const targetPath = format === "PDF" ? "/books" : "/audio-books";
      navigate(`${targetPath}?search=${encodeURIComponent(searchTerm)}`);
      handleSearchClose();
    }
  };

  const getFormatIcon = () => {
    switch (selectedFormat) {
      case 'Audiobook':
        return <HeadphonesIcon fontSize={isExtraSmall ? "small" : "medium"} color="primary" />;
      case 'Free':
        return <PriceIcon fontSize={isExtraSmall ? "small" : "medium"} color="success" />;
      default:
        return <BookIcon fontSize={isExtraSmall ? "small" : "medium"} color="primary" />;
    }
  };

  return (
    <>
      {/* Desktop Search */}
      {!fullWidth && (
        <SearchContainer ref={searchContainerRef}>
          <SearchIcon sx={{ color: "text.secondary", ml: 0.5, fontSize: '1.2rem' }} />
          <SearchInput
            placeholder={t('topBar.search.placeholder')}
            value={searchTerm}
            onChange={handleSearchChange}
            onFocus={handleSearchFocus}
            onKeyPress={handleKeyPress}
            inputProps={{ "aria-label": "search" }}
            ref={inputRef}
          />
          {searchTerm && (
            <ActionButton
              size="small"
              onClick={handleClear}
              aria-label="clear search"
              title={t('topBar.search.clear')}
            >
              <CloseIcon fontSize="small" />
            </ActionButton>
          )}
          <Tooltip title={t('topBar.search.filter')} arrow>
            <FilterButton
              size="small"
              onClick={handleFilterClick}
              aria-label="filter search"
              color="primary"
            >
              {getFormatIcon()}
            </FilterButton>
          </Tooltip>
          {showSuggestions && (
            <SearchSuggestions
              results={results}
              loading={loading}
              error={error}
              searchQuery={searchTerm}
              onSelect={handleBookSelect}
              onViewAll={handleViewAll}
              isMobile={false}
              searchHistory={searchHistory}
              onHistoryItemClick={handleHistoryItemClick}
              onHistoryItemDelete={removeFromHistory}
              onClearHistory={clearHistory}
            />
          )}
        </SearchContainer>
      )}

      {/* Mobile Search Button - Only show if not immediately searching and not fullWidth */}
      {!immediateSearch && !fullWidth && (
        <Box sx={{ display: { xs: "block", sm: "none" } }}>
          <Tooltip title={t('topBar.search.search')} arrow>
            <IconButton 
              onClick={() => setMobileSearchOpen(true)} 
              aria-label="search"
              sx={{
                transition: 'transform 0.2s ease',
                '&:hover': {
                  transform: 'scale(1.1)',
                  backgroundColor: alpha(theme.palette.primary.main, 0.08),
                },
                p: isExtraSmall ? 0.5 : 1,
              }}
            >
              <SearchIcon fontSize={isExtraSmall ? "small" : "medium"} />
            </IconButton>
          </Tooltip>
        </Box>
      )}

      {/* Mobile Search Slide */}
      <Slide
        direction="down"
        in={mobileSearchOpen || fullWidth}
        mountOnEnter
        unmountOnExit
      >
        <MobileSearchAppBar elevation={4}>
          <MobileToolbar>
            {fullWidth ? (
              <MobileSearchContainer ref={searchContainerRef}>
                <SearchIcon sx={{ 
                  color: "text.secondary", 
                  ml: 0.5,
                  fontSize: isExtraSmall ? '0.9rem' : '1.1rem'
                }} />
                <SearchInput
                  autoFocus={autoFocus}
                  fullWidth
                  placeholder={t('topBar.search.placeholder')}
                  value={searchTerm}
                  onChange={handleSearchChange}
                  onFocus={handleSearchFocus}
                  onKeyPress={(e) => {
                    if (e.key === "Enter") {
                      handleMobileSearch();
                    }
                  }}
                  inputProps={{ "aria-label": "search" }}
                  ref={inputRef}
                />
                
                {searchTerm && (
                  <ActionButton
                    size={isExtraSmall ? "small" : "medium"}
                    onClick={handleClear}
                    aria-label="clear search"
                  >
                    <CloseIcon fontSize={isExtraSmall ? "small" : "medium"} />
                  </ActionButton>
                )}
                
                <Tooltip title={t('topBar.search.filter')} arrow>
                  <FilterButton
                    size={isExtraSmall ? "small" : "medium"}
                    onClick={handleFilterClick}
                    aria-label="filter search"
                    color="primary"
                  >
                    {getFormatIcon()}
                  </FilterButton>
                </Tooltip>
                
                <IconButton 
                  size={isExtraSmall ? "small" : "medium"} 
                  onClick={handleSearchClose}
                  sx={{
                    ml: 0.5,
                    color: 'text.secondary',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      color: 'error.main',
                      backgroundColor: alpha(theme.palette.error.main, 0.1),
                    }
                  }}
                >
                  <CloseIcon fontSize={isExtraSmall ? "small" : "medium"} />
                </IconButton>
                
                {showSuggestions && (
                  <SearchSuggestions
                    results={results}
                    loading={loading}
                    error={error}
                    searchQuery={searchTerm}
                    onSelect={handleBookSelect}
                    onViewAll={handleViewAll}
                    isMobile={true}
                    searchHistory={searchHistory}
                    onHistoryItemClick={handleHistoryItemClick}
                    onHistoryItemDelete={removeFromHistory}
                    onClearHistory={clearHistory}
                  />
                )}
              </MobileSearchContainer>
            ) : (
              <>
                <IconButton 
                  edge="start" 
                  color="inherit" 
                  onClick={handleSearchClose} 
                  aria-label="back"
                  size={isExtraSmall ? "small" : "medium"}
                >
                  <ArrowBackIcon />
                </IconButton>
                
                <Box sx={{ flex: 1, mx: 1 }}>
                  <SearchWrapper elevation={0}>
                    <SearchIcon 
                      sx={{ 
                        ml: 1, 
                        color: "text.secondary",
                        fontSize: isExtraSmall ? '0.9rem' : '1.1rem'
                      }} 
                    />
                    <StyledInputBase
                      placeholder={t('topBar.search.placeholder')}
                      autoFocus
                      fullWidth
                      value={searchTerm}
                      onChange={handleSearchChange}
                      onFocus={handleSearchFocus}
                      onKeyPress={(e) => {
                        if (e.key === "Enter") {
                          handleMobileSearch();
                        }
                      }}
                      inputProps={{ "aria-label": "search" }}
                    />
                    
                    {searchTerm && (
                      <IconButton 
                        size="small" 
                        onClick={handleClear}
                        sx={{ p: isExtraSmall ? 0.25 : 0.5 }}
                      >
                        <CloseIcon 
                          fontSize={isExtraSmall ? "small" : "medium"} 
                        />
                      </IconButton>
                    )}
                    
                    <Tooltip title={t('topBar.search.filter')} arrow>
                      <FilterButton
                        size={isExtraSmall ? "small" : "medium"}
                        onClick={handleFilterClick}
                        aria-label="filter search"
                        color="primary"
                      >
                        {getFormatIcon()}
                      </FilterButton>
                    </Tooltip>
                  </SearchWrapper>
                  
                  {showSuggestions && (
                    <SearchSuggestions
                      results={results}
                      loading={loading}
                      error={error}
                      searchQuery={searchTerm}
                      onSelect={handleBookSelect}
                      onViewAll={handleViewAll}
                      isMobile={true}
                      searchHistory={searchHistory}
                      onHistoryItemClick={handleHistoryItemClick}
                      onHistoryItemDelete={removeFromHistory}
                      onClearHistory={clearHistory}
                    />
                  )}
                </Box>
              </>
            )}
          </MobileToolbar>
        </MobileSearchAppBar>
      </Slide>

      {/* Backdrop - Only show for the internal mobile search, not for fullWidth */}
      {mobileSearchOpen && !fullWidth && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            bgcolor: "rgba(0, 0, 0, 0.5)",
            zIndex: (theme) => theme.zIndex.appBar - 1,
          }}
          onClick={handleSearchClose}
        />
      )}

      <StyledMenu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleFilterClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
        elevation={4}
      >
        <MenuItem
          onClick={() => handleFormatSelect("PDF")}
          selected={selectedFormat === "PDF"}
        >
          <ListItemIcon>
            <BookIcon fontSize={isExtraSmall ? "small" : "medium"} />
          </ListItemIcon>
          <ListItemText 
            primary={t('topBar.formats.ebooks')} 
            primaryTypographyProps={{
              fontSize: isExtraSmall ? '0.8125rem' : '0.875rem',
            }}
          />
        </MenuItem>
        <MenuItem
          onClick={() => handleFormatSelect("Audiobook")}
          selected={selectedFormat === "Audiobook"}
        >
          <ListItemIcon>
            <HeadphonesIcon fontSize={isExtraSmall ? "small" : "medium"} />
          </ListItemIcon>
          <ListItemText 
            primary={t('topBar.formats.audiobooks')} 
            primaryTypographyProps={{
              fontSize: isExtraSmall ? '0.8125rem' : '0.875rem',
            }}
          />
        </MenuItem>
        <MenuItem
          onClick={() => handleFormatSelect("Free")}
          selected={selectedFormat === "Free"}
        >
          <ListItemIcon>
            <PriceIcon fontSize={isExtraSmall ? "small" : "medium"} />
          </ListItemIcon>
          <ListItemText 
            primary={t('topBar.formats.freeBooks')} 
            primaryTypographyProps={{
              fontSize: isExtraSmall ? '0.8125rem' : '0.875rem',
            }}
          />
        </MenuItem>
      </StyledMenu>
    </>
  );
};

export default SearchBar;
